import { useMemo, useState } from 'react';
import styled from '@emotion/styled';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoIcon from '@mui/icons-material/Info';
import QualQuantOverlay from './QualQuantOverlay';
import type { SegmentInsight } from '../api/client';

interface SegmentQualInsightsProps {
  segmentData: Record<string, unknown>;
  segmentName: string;
  editMode?: boolean;
}

/**
 * SegmentQualInsights — wrapper component for segment profile page
 * Reads segment insights from metadata and displays QualQuantOverlay
 */
export default function SegmentQualInsights({
  segmentData,
  segmentName,
  editMode,
}: SegmentQualInsightsProps): React.JSX.Element | null {
  const [expanded, setExpanded] = useState(true);

  // Extract insights from metadata or metadataV2
  const insight = useMemo(() => {
    const metadata = segmentData.metadata as Record<string, unknown> | undefined;
    const metadataV2 = segmentData.metadataV2 as Record<string, unknown> | undefined;

    const segmentInsights =
      (metadata?.segmentInsights as SegmentInsight | undefined) ||
      (metadataV2?.segmentInsights as SegmentInsight | undefined);

    return segmentInsights || null;
  }, [segmentData]);

  // If no insights data, don't render
  if (!insight) {
    return null;
  }

  return (
    <Root>
      <Header onClick={() => setExpanded(!expanded)}>
        <span>HCP Attributes and Traits</span>
        <ExpandIcon $expanded={expanded}>
          <ExpandMoreIcon />
        </ExpandIcon>
      </Header>
      {expanded && (
        <Content>
          <QualQuantOverlay
            insight={insight}
            segmentName={segmentName}
            defaultOpen={true}
          />
        </Content>
      )}
    </Root>
  );
}

const Root = styled.div`
  background: #ffffff;
  border-radius: 12px;
  border: 1px solid rgba(0, 0, 0, 0.06);
  margin-bottom: 24px;
`;

const Header = styled.div`
  padding: 8px 16px;
  font-size: 20px;
  font-weight: 700;
  font-family: var(--font-bold, inherit);
  color: var(--black-l, #323232);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  user-select: none;
  min-height: 64px;
  box-sizing: border-box;
`;

const ExpandIcon = styled.span<{ $expanded: boolean }>`
  color: rgba(0, 0, 0, 0.54);
  transition: transform 150ms cubic-bezier(0.4, 0, 0.2, 1);
  transform: rotate(${p => p.$expanded ? '180deg' : '0deg'});
  display: flex;
`;

const Content = styled.div`
  padding: 0 16px 16px;
`;
